import {Config} from "../config/Config";
import {Symbol} from "./Symbol";
import {Mental2SymbolStates, SYMBOL} from "../enums/Enum";
import {gsap} from "gsap";
import {TimelineSprite} from "../../core/animation/TimelineSprite";
import {GameUtils} from "../utils/GameUtils";
import {Game} from "../Game";
import {SplitSymbol} from "./SplitSymbol";
import {XWaysSymbol} from "./XWaysSymbol";
import {BitMapMultiplier} from "../components/BitmapMultiplier";
import {GameAssets} from "../GameAssets";
import {Sound} from "../sound/Sound";
import {XMentalSymbol} from "./XMentalSymbol";
import {TombStoneSymbol} from "./TombStoneSymbol";
import {DeadSymbol} from "./DeadSymbol";
import {XGodSymbol} from "./XGodSymbol";
import {NudgeSymbol} from "./NudgeSymbol";

export class SymbolContainer extends PIXI.Container {
    public _xHoleMultiplier: BitMapMultiplier | undefined;
    private _isBottomHalf: boolean = false;
    private readonly _reelId: number;
    private readonly _rowId: number;
    private _symbolName: string;
    private _isTopHalf: boolean = false;
    private _symbols: Symbol[] = [];

    constructor(symbolName: string, initState: Mental2SymbolStates, reelId: number, reelPosition: number) {
        super();
        this._reelId = reelId;
        this._rowId = reelPosition;
        this.setHalfStuffs();
        this._symbolName = symbolName;
        this.name = symbolName + "_" + reelPosition + (this._isTopHalf ? "_half" : "");

        if (this._isTopHalf) {
            this.createTopHalfSym();
        } else {
            const sym: Symbol = this.createSymbol(symbolName, initState, 0);
            if (Mental2SymbolStates.NORMAL === initState) {
                sym.setStateAsPerParts(1);
            }
            this._symbols.push(sym);
            this.addChild(sym);
        }
    }

    public revealEnhancerSymbol(toSym: string, parts: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        this._symbolName = toSym;
        if (toSym === SYMBOL.DEAD) {
            Game.model.deadSymCount++;
            Game.reelView.addDeadSymInfo(this._reelId + "_" + this._rowId);
        }
        tl.add(this.split(parts, toSym), 0);
        if (toSym === SYMBOL.DEAD && Game.model.deadSymCount > 1) {
            tl.add(Game.reelView.doActivateDeadSymbols(), 0.4);
        }
        return tl;
    }

    public doActiveDeadSym(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        this._symbols.forEach((sym: Symbol, subRowId: number) => {
            if (sym.symbolName === SYMBOL.DEAD) {
                tl.add(() => {
                    const toSymbol: Symbol = this.createSymbol(sym.symbolName, Mental2SymbolStates.NORMAL, subRowId, true);
                    const fromSym: Symbol = this._symbols[subRowId];
                    const y: number = fromSym.y;
                    const scaleY: number = fromSym.scale.y;
                    let lastState: string = fromSym.state.tracks[0].animation.name;
                    if (lastState.indexOf("_loop") > -1) {
                        lastState = lastState.replace("_loop", "");
                    }
                    toSymbol.y = y;
                    toSymbol.setResizeState(lastState);
                    toSymbol.scale.y = scaleY;
                    toSymbol.currentHeight = fromSym.currentHeight;
                    this.removeChild(fromSym);
                    fromSym.removeSymbol();
                    this._symbols[subRowId] = toSymbol;
                    this.addChild(toSymbol);
                });
            }
        });
        tl.to({}, {duration: 0.6}); //dead active animation time
        return tl;
    }

    public splitByFeature(splitIndex: number, multiplier: number[] = []): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        const symArr: Symbol[] = [];
        let _splitSymArr: string[] = [];
        let _splitSymMulArr: number[] = [];
        splitIndex = Math.min(splitIndex, this.symbols.length - 1);
        this.symbols.forEach((sym: Symbol, index: number) => {
            _splitSymMulArr.push(sym.getMultiplier());
            _splitSymArr.push(sym.symbolName);
            if (splitIndex === index) {
                if (sym.symbolName === SYMBOL.DEAD || sym.symbolName === SYMBOL.DEAD_ACTIVE) {
                    Game.model.deadSymCount++;
                }
                _splitSymArr.push(sym.symbolName);
                _splitSymMulArr.push(0);
            }
        });
        let segments: number = _splitSymArr.length;
        const isActive: boolean = Game.model.deadSymCount > 1;
        if (this._isTopHalf || this._isBottomHalf) {
            _splitSymArr = _splitSymArr.concat(_splitSymArr);
            _splitSymMulArr = _splitSymMulArr.concat(_splitSymMulArr);
            segments = _splitSymArr.length;
        }

        for (let i: number = 0; i < _splitSymArr.length; i++) {
            const symbol: Symbol = this.createSymbol(_splitSymArr[i], Mental2SymbolStates.NORMAL, i, isActive);
            symArr.push(symbol);
            symbol.setStateAsPerParts(segments);
            const symHeight: number = Config.SYMBOL_HEIGHT / segments;
            const y: number = (symHeight * (i + 1)) - 99 - (symHeight * 0.5);
            symbol.y = y;
            if (_splitSymMulArr[i] > 1) {
                symbol.addDeadMultiplier(_splitSymMulArr[i]);
            }
        }
        this.beforeSplit();
        this.addChild(...symArr);
        this._symbols.push(...symArr);
        return tl;
    }

    public revealMysterySymbol(revealSymbol: string, multiplier: number[] = [],): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        const symArr: Symbol[] = [];
        const _splitSymArr: string[] = [];
        this._symbols.forEach((sym: Symbol, index: number) => {
            if (sym.symbolName === SYMBOL.X_WAYS) {
                _splitSymArr.push(revealSymbol);
                _splitSymArr.push(revealSymbol);
                _splitSymArr.push(revealSymbol);
            } else {
                _splitSymArr.push(sym.symbolName);
            }

        });
        let segments: number = _splitSymArr.length;
        const isActive: boolean = Game.model.deadSymCount > 1;
        if (this._isTopHalf || this._isBottomHalf) {
            segments = segments * 2;
        }

        for (let i: number = 0; i < _splitSymArr.length; i++) {
            const symbol: Symbol = this.createSymbol(_splitSymArr[i], Mental2SymbolStates.NORMAL, i, isActive);
            tl.add(() => {
                symArr.push(symbol);
                symbol.setStateAsPerParts(segments);
            });
            const symHeight: number = Config.SYMBOL_HEIGHT / segments;
            const y: number = (symHeight * (i + 1)) - 99 - (symHeight * 0.5);
            tl.to(symbol, {duration: 0.1, y: y}, 0.01);
        }
        tl.add(() => {
            this.beforeSplit();
            this.addChild(...symArr);
            this._symbols.push(...symArr);
        })
        return tl;
    }

    public xHoleSplit(_splitSymArr: string[], multiplier: number[] = [],): void {
        const symArr: Symbol[] = [];
        let segments: number = _splitSymArr.length;
        const isActive: boolean = segments > 1;
        let expandedYAdjustment: number = 0;
        if (this._isTopHalf || this._isBottomHalf) {
            segments = segments * 2;
            _splitSymArr = _splitSymArr.concat(_splitSymArr);
        }
        let currentHeightTotal: number = 0;
        this._symbols.forEach((sym: Symbol, index: number) => {
            currentHeightTotal += sym.currentHeight;
        })

        for (let i: number = 0; i < _splitSymArr.length; i++) {
            const symbol: Symbol = this.createSymbol(_splitSymArr[i], Mental2SymbolStates.NORMAL, i, isActive);
            symArr.push(symbol);
            symbol.setStateAsPerParts(segments)
            const symHeight: number = currentHeightTotal / segments;
            const y: number = (symHeight * (i + 1)) - 99 - (symHeight * 0.5) + expandedYAdjustment;
            symbol.y = y;
            if (multiplier.length) {
                symbol.addDeadMultiplier(multiplier[i]);
            }
        }
        this.beforeSplit();
        this.addChild(...symArr);
        this._symbols.push(...symArr);
    }

    public xHoleToWild(_splitSymArr: string[], multiplier: number[] = [],): void {
        const symArr: Symbol[] = [];
        let segments: number = _splitSymArr.length;
        let currentHeightTotal: number = 0;
        this._symbols.forEach((sym: Symbol) => {
            currentHeightTotal += sym.currentHeight;
        });
        const eachSymHeight: number = currentHeightTotal / segments;

        for (let i: number = 0; i < _splitSymArr.length; i++) {
            const symbol: Symbol = this.createSymbol(_splitSymArr[i], Mental2SymbolStates.NORMAL, i, false);
            symArr.push(symbol);
            symbol.setStateAsPerParts(segments, eachSymHeight)
            const symHeight: number = eachSymHeight;
            const y: number = (symHeight * (i + 1)) - 99 - (symHeight * 0.5);
            symbol.y = y;
            if (multiplier.length) {
                symbol.addDeadMultiplier(multiplier[i]);
            }
        }
        this.beforeSplit();
        this.addChild(...symArr);
        this._symbols.push(...symArr);
    }

    public revealMysterySymbolFromS2(revealSymbol: string, multiplier: number[] = [],): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        const symArr: Symbol[] = [];
        const _splitSymArr: string[] = [];

        const startY: number = this.symbols[0].y - (this.symbols[0].currentHeight * 0.5);
        const totalHeight: number = this.symbols[0].currentHeight * this.symbols.length;
        let isActive: boolean = false;
        let _splitSymMulArr: number[] = [];

        this._symbols.forEach((sym: Symbol, index: number) => {
            if (sym.symbolName === SYMBOL.X_WAYS) {
                _splitSymArr.push(revealSymbol);
                _splitSymArr.push(revealSymbol);
                _splitSymArr.push(revealSymbol);
                _splitSymMulArr.push(0);
                _splitSymMulArr.push(0);
                _splitSymMulArr.push(0);
            } else {
                if (sym.symbolName === SYMBOL.DEAD_ACTIVE) {
                    isActive = true;
                }
                _splitSymArr.push(sym.symbolName);
                _splitSymMulArr.push(sym.getMultiplier());
            }

        });
        let segments: number = _splitSymArr.length;
        const perSymHeight: number = totalHeight / _splitSymArr.length;
        for (let i: number = 0; i < _splitSymArr.length; i++) {
            const symbol: Symbol = this.createSymbol(_splitSymArr[i], Mental2SymbolStates.NORMAL, i, isActive);
            tl.add(() => {
                symArr.push(symbol);
                symbol.setStateAsPerParts(segments, perSymHeight);
            });
            let splitSymY: number = startY + (perSymHeight * 0.5) + (perSymHeight * i)
            tl.to(symbol, {duration: 0.1, y: splitSymY}, 0.01);
            if (_splitSymMulArr[i] > 1) {
                tl.add(() => {
                    symbol.addDeadMultiplier(_splitSymMulArr[i]);
                });
            }
        }
        tl.add(() => {
            this.beforeSplit();
            this.addChild(...symArr);
            this._symbols.push(...symArr);
        })
        return tl;
    }

    public split(segments: number, revealSymbol: string, multiplier: number[] = []): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        const symArr: Symbol[] = [];
        const isActive: boolean = Game.model.deadSymCount > 1;
        if (this._isTopHalf || this._isBottomHalf) {
            segments = segments * 2;
        }

        for (let i: number = 0; i < segments; i++) {
            const symbol: Symbol = this.createSymbol(revealSymbol, Mental2SymbolStates.NORMAL, i, isActive);
            tl.add(() => {
                symArr.push(symbol);
                symbol.setStateAsPerParts(segments);
            });
            const symHeight: number = Config.SYMBOL_HEIGHT / segments;
            const y: number = (symHeight * (i + 1)) - 99 - (symHeight * 0.5);
            tl.to(symbol, {duration: 0.1, y: y}, 0.01);
        }
        tl.add(() => {
            this.beforeSplit();
            this.addChild(...symArr);
            this._symbols.push(...symArr);
        })
        return tl;
    }

    public expandFeature(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        let pivotY: number = 99;
        tl.add(() => {
            //this._isBottomHalf = false; //now it will be 1 full symbol //moved to show showEnhancerFeature function
            const symHeight: number = Config.SYMBOL_HEIGHT;
            this._symbols[0].y = symHeight - 99 - (symHeight * 0.5);
            this._symbols[0].setStateAsPerParts(1);
        });
        tl.to(this.pivot, {duration: 0.1, y: pivotY}, 0);
        return tl;
    }

    // }
    public showEnhancerFeature(segments: number, triggeredFeature: SYMBOL): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        this._symbolName = triggeredFeature;
        tl.add(() => {
            this.beforeSplit();
        });
        if (triggeredFeature === SYMBOL.DEAD) {
            Game.model.deadSymCount++;
        }
        const state: Mental2SymbolStates = triggeredFeature === SYMBOL.X_WAYS ? Mental2SymbolStates.ENHANCER_REVEAL : Mental2SymbolStates.NORMAL;
        const symbol: SplitSymbol | XWaysSymbol = this.createSymbol(triggeredFeature, state, 0, Game.model.deadSymCount > 1) as (SplitSymbol | XWaysSymbol);
        symbol.playEnhancerReveal();
        const symHeight: number = Config.SYMBOL_HEIGHT / segments;
        const y: number = symHeight - 99 - (symHeight * 0.5);
        symbol.y = y;
        tl.add(() => {
            this.addChild(symbol)
            this._symbols.push(symbol);
            this._isBottomHalf = false; //now it will be 1 full symbol
        });
        return tl;
    }

    public setState(state: Mental2SymbolStates, toHeight: number = 198) {
        this._symbols.forEach((symbol: Symbol) => {
            symbol.setState(state);
        });
    }

    public symChangeEffect(subRowId: number = 0): gsap.core.Timeline {
        const effectTl: gsap.core.Timeline = new gsap.core.Timeline();
        const symChangeTls: TimelineSprite = GameUtils.createTimelineSprite("sysms_change_effect/sysms_change_effect_000", 30, undefined, true)
        symChangeTls.height = this._symbols[subRowId].currentHeight * 2;
        symChangeTls.name = "symChangeTls_" + 0;
        symChangeTls.scale.set(2);
        const pos: PIXI.Point = GameUtils.getSymbolPos(this._reelId, this._rowId);
        if (this._isTopHalf) {
            pos.y = pos.y - 49;
        }
        pos.y -= this.pivot.y;
        effectTl.add(() => {
            Game.effectsView.addChild(symChangeTls);
            symChangeTls.position.set(pos.x + 5, this._symbols[subRowId].y + pos.y);
        }, 0);
        effectTl.add(symChangeTls.getAnimationAutoShowHide(true, true), 0);
        effectTl.add(() => {
            symChangeTls.parent.removeChild(symChangeTls);
        });
        return effectTl;
    }

    public playXWaysEffects(sym: Symbol, subRowId: number): gsap.core.Timeline {
        const effectTl: gsap.core.Timeline = new gsap.core.Timeline();
        const pos: PIXI.Point = GameUtils.getSymbolPos(this._reelId, this._rowId);
        if (this._isTopHalf) {
            pos.y = pos.y - 49;
        }
        pos.y -= this.pivot.y;

        const xWaysEffectSpine: PIXI.spine.Spine = new PIXI.spine.Spine(Game.assetLoader.getSpineAsset("xWaysEffects"))
        const xWayWinOverlaySpine: PIXI.spine.Spine = new PIXI.spine.Spine(Game.assetLoader.getSpineAsset("xWaysWinOverlayEffects"));
        xWayWinOverlaySpine.height = sym.currentHeight * 1.52;
        xWayWinOverlaySpine.name = "xWayWinOverlaySpine" + subRowId;
        //xWaysEffectSpine.height = this._symbols[0].currentHeight;
        xWaysEffectSpine.name = "xWaysEffectSpine" + subRowId;
        let animName: string = sym.state.tracks[0].animation.name;
        animName = animName.split("_")[0] + "_win";

        effectTl.add(() => {
            Game.effectsView.addChild(xWaysEffectSpine);
            Game.effectsView.addChild(xWayWinOverlaySpine);
            xWaysEffectSpine.position.set(pos.x, sym.y + pos.y);
            xWayWinOverlaySpine.position.set(pos.x, sym.y + pos.y);
        }, 0);
        effectTl.add([
            GameUtils.createTimelineForAnimation(xWaysEffectSpine, 0, animName, false),
            GameUtils.createTimelineForAnimation(xWayWinOverlaySpine, 0, "198_win", false)
        ], 0);
        effectTl.add(() => {
            xWayWinOverlaySpine.parent.removeChild(xWaysEffectSpine, xWayWinOverlaySpine);
        });
        return effectTl;
    }

    public revealDeadSym(revealTo: string, subRowId: number, multiplier: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        if (this._isTopHalf) {
            subRowId += this.symbols.length;
        }
        tl.add(this.playDeadBlast(subRowId), 0.1);
        tl.add(() => {
            const random: number = Math.floor(Math.random() * (Sound.DEAD_SYMBOL_REVEAL.length));
            Game.soundPlayer.playSound(Sound.DEAD_SYMBOL_REVEAL[random]);
        }, 0.1);
        tl.add(this.changeSymbol(subRowId, revealTo), 0.3);
        tl.add(this.handleDeadMultiplier(subRowId, revealTo, multiplier));
        return tl;
    }

    public changeSymbol(subRowId: number, toSymName: string): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        if (toSymName === SYMBOL.DEAD) {
            Game.model.deadSymCount++;
            Game.reelView.addDeadSymInfo(this._reelId + "_" + this._rowId);
        }

        const toSymbol: Symbol = this.createSymbol(toSymName, Mental2SymbolStates.NORMAL, subRowId, Game.model.deadSymCount > 1);
        const fromSym: Symbol = this._symbols[subRowId];
        const y: number = fromSym.y;
        const scaleY: number = fromSym.scale.y;
        let lastState: string;
        if (fromSym.symbolName === SYMBOL.X_HOLE && toSymName === SYMBOL.WILD) {
            lastState = "198_to_198";
        } else {
            lastState = fromSym.state.tracks[0].animation.name;
        }
        if (lastState.indexOf("_loop") > -1) {
            lastState = lastState.replace("_loop", "");
        }
        const currentHeight: number = fromSym.currentHeight;
        tl.add(() => {
            toSymbol.y = y;
            toSymbol.setResizeState(lastState);
            toSymbol.scale.y = scaleY;
            toSymbol.currentHeight = currentHeight;
            this.removeChild(fromSym);
            fromSym.removeSymbol();
            this._symbols[subRowId] = toSymbol;
            this.addChild(toSymbol);
        });
        return tl;
    }

    public getLatestSplitIndex(): number {
        let splitIndex: number = -1;
        for (let symIndex: number = 0; symIndex < this._symbols.length; symIndex++) {
            if (this._symbols[symIndex].symbolName === SYMBOL.X_SPLIT) {
                splitIndex = symIndex;
                break;
            }
        }
        return splitIndex;
    }

    destroy(options?: { children?: boolean; texture?: boolean; baseTexture?: boolean }) {
        if (this._xHoleMultiplier) {
            this.removeChild(this._xHoleMultiplier);
            this._xHoleMultiplier = undefined;
        }

        this._symbols.forEach((sym) => {
            sym.removeSymbol();
        });
        super.destroy(options);
    }

    public setLines(parts: number): void {
        if (parts > 8) {
            this.split(8, this.symbolName).add(() => {
                const linePerSymbol: number = Math.ceil((parts - 8) / 8);
                const neededParts: number = Math.ceil(parts / 8);
                let symIndex: number = -1;
                let lineIndex: number = 0;
                for (let i = 0; i < parts - 8; i++) {
                    const lineTLS: TimelineSprite = GameUtils.createTimelineSprite("split_line_effect/split_line_effect_000", 30, undefined, true);
                    lineTLS.scale.y = (this._reelId === 2 && this._rowId === 0) ? 1 : 0.3;
                    if (i % linePerSymbol === 0) {
                        symIndex++;
                        lineIndex = 0;
                    }
                    const sym: Symbol = this.symbols[symIndex];
                    const startY: number = sym.y - (sym.currentHeight * 0.5);
                    const perSymHeight: number = sym.currentHeight / neededParts;
                    let splitSymY: number = -(sym.currentHeight * 0.5) + (perSymHeight * ((lineIndex + 1) % neededParts))
                    lineIndex++;
                    sym.addChild(lineTLS);
                    lineTLS.getAnimationAutoShowHide(true, false);
                    lineTLS.position.set(sym.x, splitSymY);
                }
            });
        }
    }

    public hideTopHalf(value: boolean): void {
        this._symbols.forEach((sym: Symbol) => {
            sym.visible = !value;
        });
        this.symbols.forEach((sym: Symbol) => {
            sym.visible = true;
        })
    }

    public createSymbol(symbolName: string, initState: Mental2SymbolStates, index: number, isActive: boolean = false): Symbol {
        let sym: Symbol;
        let isDeadAlreadyActive: boolean = symbolName === SYMBOL.DEAD_ACTIVE;
        if (symbolName === SYMBOL.DEAD) {
            symbolName = isActive ? SYMBOL.DEAD_ACTIVE : symbolName;
        }

        if (symbolName === SYMBOL.GOD) {
            sym = new XGodSymbol(symbolName, initState, index, isActive);
        } else if (symbolName === SYMBOL.X_MENTAL) {
            sym = new XMentalSymbol(symbolName, initState, index, isActive);
        } else if (symbolName.includes("TS")) {
            sym = new TombStoneSymbol(symbolName, initState, index, isActive);
        } else {
            sym = Game.graphicsPool.getSymbolPool(symbolName).withdraw();
            if (symbolName === SYMBOL.DEAD_ACTIVE) {
                (sym as DeadSymbol).init(symbolName, initState, index, isDeadAlreadyActive);
            } else {
                sym.init(symbolName, initState, index);
                if (symbolName === SYMBOL.X_NUDGE) {
                    (sym as NudgeSymbol).setPivot(this._reelId);
                }
            }
        }
        return sym;
    }

    public createXHoleMul(value: number) {
        this._xHoleMultiplier = new BitMapMultiplier(GameAssets.BITMAP_FONT_X_HOLE_MULTIPLIER, 28, "x");
        let height: number = this.symbols[0].currentHeight * this.symbols.length;
        const symbolMidY: number = this.symbols[0].y - (this.symbols[0].currentHeight * 0.5) + (height * 0.5);
        this._xHoleMultiplier.position.y = symbolMidY - 10;
        this._xHoleMultiplier.anchor = new PIXI.Point(0.5, 0.5);
        this._xHoleMultiplier.visible = false;
        this._xHoleMultiplier.name = "_xHoleMultiplier";
        this._xHoleMultiplier.value = value;
        this.addChild(this._xHoleMultiplier);
    }

    public fadeInXHoleMul(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        if (this._xHoleMultiplier) {
            tl.add([
                () => {
                    if (this._xHoleMultiplier) {
                        this._xHoleMultiplier.visible = true;
                        this._xHoleMultiplier.alpha = 0;
                    }
                },
                gsap.to(this._xHoleMultiplier, {duration: 0.1, alpha: 0.6}) //#403 made more transparent
            ], 0);
        }
        return tl;
    }

    public fadeOutXHoleMul(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        if (this._xHoleMultiplier) {
            tl.to(this._xHoleMultiplier, {duration: 0.1, alpha: 0});
        }
        return tl;
    }

    public hideBottomHalfs(value: boolean): void {
        if (this._isBottomHalf) {
            this._symbols.slice(this._symbols.length / 2, this._symbols.length).forEach((sym: Symbol) => {
                sym.visible = value;
            });
        }
    }

    private handleDeadMultiplier(subRowId: number, revealTo: string, multiplier: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        if (multiplier !== 1) {
            tl.add(() => {
                this._symbols[subRowId].addDeadMultiplier(multiplier);
            });
            const sourcePos: PIXI.Point = GameUtils.getSymbolPos(this._reelId, this._rowId);
            if (this._isTopHalf) {
                sourcePos.y = sourcePos.y - 49 - 49; //extra 49 for Config.SYMBOL_WIDTH * 0.5 line
            }
            const deadMulPos: PIXI.Point = new PIXI.Point(Config.SYMBOL_WIDTH * 0.5, this._symbols[subRowId].currentHeight * 0.5 - 10);
            sourcePos.x += this._symbols[subRowId].x + deadMulPos.x - 30;
            sourcePos.y += this._symbols[subRowId].y + deadMulPos.y + 15;
            tl.add(Game.stickyView.updateStickyDeadMultiplier(sourcePos, revealTo as SYMBOL), 0);
        }
        return tl;
    }

    private createTopHalfSym(): void {
        for (let i: number = 0; i < 2; i++) {
            const symbol: Symbol = this.createSymbol(this._symbolName, Mental2SymbolStates.NORMAL, i, false);
            symbol.setStateAsPerParts(2);
            const symHeight: number = Config.SYMBOL_HEIGHT / 2;
            symbol.y = (symHeight * (i + 1)) - 99 - (symHeight * 0.5);
            this.addChild(symbol);
            this._symbols.push(symbol)
        }
    }

    private playDeadBlast(subRowId: number): gsap.core.Timeline {
        const effectTl: gsap.core.Timeline = new gsap.core.Timeline();

        const pos: PIXI.Point = GameUtils.getSymbolPos(this._reelId, this._rowId);
        if (this._isTopHalf) {
            pos.y = pos.y - 49;
        }
        const symLength: number = this._symbols.length;
        let isVisibleSym: boolean = true;
        if ((this._isTopHalf && subRowId < (symLength / 2)) || (this._isBottomHalf && subRowId >= (symLength / 2))) {
            isVisibleSym = false;
        }
        const symHeight: number = this._symbols[subRowId].currentHeight;
        const y: number = this._symbols[subRowId].y;
        if (isVisibleSym) {
            const deadBlastTls: TimelineSprite = GameUtils.createTimelineSprite("dead_blast/dead_blast_000", 25, undefined, true)
            deadBlastTls.height = symHeight + 30;
            deadBlastTls.name = "symChangeTls_" + subRowId;
            deadBlastTls.scale.set(1);
            effectTl.add(() => {
                Game.effectsView.addChild(deadBlastTls);
                deadBlastTls.position.set(pos.x + 5, y + pos.y);
            }, 0);
            effectTl.add(deadBlastTls.getAnimationAutoShowHide(true, true), 0);
            effectTl.add(() => {
                deadBlastTls.parent.removeChild(deadBlastTls);
            });
        }
        return effectTl;
    }

    private setHalfStuffs(): void {
        this._isTopHalf = this._reelId === 2 && this._rowId === 0;
        if ((this._reelId === 1 || this._reelId === 3) && this._rowId === 2) {
            this._isBottomHalf = true;
        } else if (this._reelId === 2 && this._rowId === 3) {
            this._isBottomHalf = true;
        }
    }

    private beforeSplit(): void {
        this._symbols.forEach((sym) => {
            if (sym.name.indexOf(SYMBOL.X_SPLIT) > -1) {
                sym.symbolName = SYMBOL.X_SPLIT;
            }
            sym.removeSymbol();
        });
        this.removeChildren();
        this._symbols.length = 0;
    }

    get reelId(): number {
        return this._reelId;
    }

    get rowId(): number {
        return this._rowId;
    }

    get symbolName(): string {
        return this._symbolName;
    }

    get isTopHalf(): boolean {
        return this._isTopHalf;
    }

    get symbols(): Symbol[] {
        if (this._isTopHalf) {
            return this._symbols.slice(this._symbols.length / 2);
        } else if (this._isBottomHalf) {
            return this._symbols.slice(0, this._symbols.length / 2);
        } else {
            return this._symbols;
        }
    }

}